import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TelegramErrorReporterService } from './telegram-error-reporter.service';

/**
 * Angular's default ErrorHandler, plus a Telegram alert for every uncaught error.
 *
 * Extends rather than replaces the default so the browser console output is unchanged.
 * (The older `GlobalErrorHandler` in `global-error-handler/` was never registered and
 * swallows errors, so it is deliberately not reused.)
 */
@Injectable()
export class TelegramErrorHandler extends ErrorHandler {

  constructor(
    private injector: Injector,
    private telegramReporter: TelegramErrorReporterService
  ) {
    super();
  }

  /**
   * Logs the error the way Angular does by default, then reports it.
   * @param {unknown} error whatever was thrown or rejected
   * @returns {void}
   */
  override handleError(error: unknown): void {
    super.handleError(error);

    try {
      const normalized = this.normalizeError(error);
      this.telegramReporter.report({
        type: 'GLOBAL_RUNTIME',
        message: normalized.message,
        name: normalized.name,
        stack: normalized.stack ?? null,
        route: this.getCurrentRoute()
      });
    } catch {
      // Reporting must never turn one error into two.
    }
  }

  // Router is resolved lazily: the ErrorHandler is built very early, and an error thrown
  // during Router setup would otherwise fail to construct the handler that reports it.
  private getCurrentRoute(): string {
    try {
      return this.injector.get(Router).url;
    } catch {
      return '/';
    }
  }

  private normalizeError(error: unknown): Error {
    // zone.js wraps an uncaught promise rejection; the real reason is on `rejection`.
    const unwrapped = error && typeof error === 'object' && 'rejection' in error
      ? (error as { rejection: unknown }).rejection
      : error;

    if (unwrapped instanceof Error) {
      return unwrapped;
    }

    if (typeof unwrapped === 'string') {
      return new Error(unwrapped);
    }

    if (unwrapped && typeof unwrapped === 'object') {
      const record = unwrapped as Record<string, unknown>;
      const normalized = new Error(typeof record['message'] === 'string' ? record['message'] : 'Unknown client-side error');
      normalized.name = typeof record['name'] === 'string' ? record['name'] : 'Error';
      if (typeof record['stack'] === 'string') {
        normalized.stack = record['stack'];
      }
      return normalized;
    }

    return new Error('Unknown client-side error');
  }
}
